import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import useProducts from 'hooks/useProducts';
import { classNames } from 'utils';
import HorizontalScroll from '../../HorizontalScroll/HorizontalScroll';
import NewProduct from './NewProduct';
import Badge from 'ui-kit/Badge';

import './Store.scss';
import { Typography } from 'ui-kit';
import SwitchNew from 'ui-kit/SwitchNew';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

interface IStore {
  className?: string;
  storeId?: number;
  hasClosedTutorial: boolean;
  page: 'all' | 'products' | 'local-products' | 'in-home';
  children: (
    products,
    newLocalProducts,
    isLoading,
    mutate,
    productsAreReordering,
  ) => React.ReactNode;
}

export default function Store({
  className,
  storeId,
  page,
  children,
  hasClosedTutorial,
}: IStore): React.ReactElement {
  const [productsAreReordering, setProductsAreReordering] = useState(false);

  const { isLoading, products, notifyMe, newLocalProducts, mutate } = useProducts({
    storeId,
    hasClosedTutorial,
  });

  const showReorderToggel = useMemo(() => page === 'all', [page]);

  useEffect(() => {
    if (page !== 'all' && productsAreReordering) {
      setProductsAreReordering(false);
    }
  }, [page, productsAreReordering]);

  const items = [
    <div className={`DashboardStore_menuItem${page === 'all' ? ' selected' : ''}`} key="all">
      <Link
        to={reverse({
          url: URLS.STORE_DETAILS,
          params: { storeId },
        })}
      >
        All
      </Link>
    </div>,
    <div
      className={`DashboardStore_menuItem${page === 'in-home' ? ' selected' : ''}`}
      key="in-home"
    >
      <Link
        to={reverse({
          url: URLS.STORE_PRODUCTS_BY_CATEGORY,
          params: { storeId, category: 'in-home' },
        })}
      >
        My products
      </Link>
    </div>,
    <Badge key="local-products" variant="dot" color="error" invisible={!notifyMe}>
      <div className={`DashboardStore_menuItem${page === 'local-products' ? ' selected' : ''}`}>
        <Link
          to={reverse({
            url: URLS.STORE_PRODUCTS_BY_CATEGORY,
            params: { storeId, category: 'local-products' },
          })}
        >
          Local services
        </Link>
      </div>
    </Badge>,
  ];

  const handleChangeReordering = useCallback((checked) => setProductsAreReordering(checked), []);

  return (
    <div className={classNames(className, 'DashboardStore')}>
      <div className="DashboardStore_controls">
        <div className="DashboardStore_menu">
          <HorizontalScroll items={items} hideArrows />
          <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
            {showReorderToggel && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Typography variant="body1" className="DashboardStore_dragAndDropTitle">
                  Drag and drop tiles
                </Typography>
                <SwitchNew checked={productsAreReordering} onChange={handleChangeReordering} />
              </div>
            )}
            <NewProduct storeId={storeId} />
          </div>
        </div>
      </div>

      <div className="DashboardStore_content">
        {children(products, newLocalProducts, isLoading, mutate, productsAreReordering)}
      </div>
    </div>
  );
}
